import { renderClipFrame, renderTransitionFrame, renderOverlayLayer } from '../compositor/canvasCompositor.js'
import { getTransitionAt } from '../compositor/transitions.js'
import { configureHighQualityContext } from '../export/qualitySettings.js'
import {
    getMediaTrack,
    getAudioTrack,
    getActiveClipAtMs,
    mapEffectiveMsToSourceMs,
    mapSourceMsToEffectiveMs,
    findNextEnabledSegmentStart,
    computeTimelineDurationMs,
    getEffectiveClipDuration
} from '../timelineModel.js'

// How far ahead of a plain (transition-less) cut to start seeking the next clip's video element -
// see preSeekUpcomingClip. A real seek can take anywhere from ~100ms to several hundred ms to
// actually settle (worse for the very first seek on a freshly preloaded element), all of which
// used to be spent frozen on the outgoing clip's last frame since the seek only ever started right
// at the cut itself. Generous on purpose: starting early has no real downside (it's just a seek,
// not playback) whereas starting late is exactly the stutter this exists to avoid.
const CUT_PRESEEK_LOOKAHEAD_MS = 600

// A load failure is often just a transient network blip (rather than a genuinely broken/expired
// URL) - retrying a few times on its own, silently, resolves most of those without ever bothering
// the user. Only once every automatic attempt has also failed does onMediaError actually fire
// (see getOrCreateMedia) and put the ball in the user's court via the "Réessayer" banner.
const MAX_AUTO_MEDIA_RETRIES = 3
const AUTO_MEDIA_RETRY_DELAY_MS = 700

// Applies a clip's speed to its <video> element, including whether its audio should keep its
// original pitch (the browser's own automatic pitch correction, on by default) or let pitch shift
// naturally with speed (a plain resample) - mirrors the toggle exposed in ClipToolPanel's
// "Vitesse" tool, and matches the same choice audioMixer.js applies for the export.
const applyClipPlaybackRate = (el, clip) => {
    el.playbackRate = clip.playbackRate || 1
    const preserve = clip.preservePitch !== false
    el.preservesPitch = preserve
    // Older/prefixed names some browsers still expect - setting properties that don't exist on a
    // given element is a harmless no-op, so all three can just always be set.
    el.mozPreservesPitch = preserve
    el.webkitPreservesPitch = preserve
}

// Drives live preview playback of a multi-clip timeline: owns a pool of hidden <video>/<img>
// elements (one per unique fileId, lazily created), advances the playhead, keeps the active
// clip's native audio audible while muting the rest, and renders each frame (clip + transition +
// overlay layer) onto the visible canvas.
//
// Key perf rule: while PLAYING, never force a seek+wait every frame - that serializes every
// frame behind a decode round-trip and is what causes visible/audible stutter. Instead, let the
// active clip's <video> play natively and just read whatever frame it has currently decoded.
// Seeking only happens on real one-off events: starting playback, switching to a new clip,
// scrubbing, or skipping a disabled (deleted/silence-removed) segment.
export class TimelinePlayer {
    constructor({ canvasEl, getTimeline, resolveSource, onTimeUpdate, getOverlayCanvasEl, getCropPreviewClipId, onMediaError }) {
        this.canvasEl = canvasEl
        this.ctx = canvasEl.getContext('2d')
        this.getTimeline = getTimeline
        this.resolveSource = resolveSource
        this.onTimeUpdate = onTimeUpdate
        this.getOverlayCanvasEl = getOverlayCanvasEl
        // A getter, not a value pushed in via a setter: every draw path (the initial refresh, the
        // media 'load' handler in getOrCreateMedia, seek, tick, scrub) has to agree on whether the
        // crop tool is open, and a stored copy only agrees while whoever owns it remembers to push
        // every change - which is exactly the ordering the crop tool loses on startup, since
        // MediaEditor.vue opens it before this player exists. Reading it live makes the question
        // unanswerable-in-the-wrong-way instead. Same pattern as getTimeline/getOverlayCanvasEl.
        this.getCropPreviewClipId = getCropPreviewClipId || (() => null)
        // Called (fileId) when a media element fails to actually load (bad/expired URL, network
        // failure, unsupported codec...) - previously nothing detected this at all: `entry.ready`
        // just stayed false forever, silently, leaving the preview *and* the timeline filmstrip
        // (thumbnails.js seeks the same failed element) black with no feedback and no way to
        // recover short of closing and reopening the whole editor.
        this.onMediaError = onMediaError

        // Keyed by clip.id (not fileId): splitting a clip produces two clips that share the same
        // source file, and a transition between them (or between a split half and a neighboring
        // clip that happens to reuse the same file) needs to show two *different* frames of that
        // file at once - sharing one <video> element per fileId meant seeking it to two positions
        // "at once" just raced, leaving both sides of the transition showing the same frame.
        this.mediaPool = new Map() // clip.id -> { kind, el, ready }
        this.playing = false
        this.destroyed = false
        this.currentMs = 0
        this.rafHandle = null
        this.lastTickAt = null
        this.backgroundAudioEls = new Map() // audio clip id -> <audio>
        this.backgroundAudioFileIds = new Map() // audio clip id -> fileId currently loaded

        this.activeClip = null
        this.activeEntry = null
        this.transitionToClip = null
        this.transitionToEntry = null
        this.switchingToClipId = null
        // Which upcoming clip (if any) has already had its seek kicked off ahead of the actual cut
        // - see preSeekUpcomingClip. Without this, seekMediaTo would get re-issued on every single
        // tick during the whole lookahead window, restarting the browser's own in-progress seek
        // each time instead of ever letting one finish.
        this.preSeekedClipId = null
        this.playToken = 0
        this.skippingToSegmentStart = null
        // Wall-clock anchor used once the active clip's own video has plateaued at its natural end
        // (see the plateau handling in tick()) - null whenever not currently plateaued.
        this.overrunAnchorRealMs = null
        this.overrunAnchorGlobalMs = null

        this.tick = this.tick.bind(this)
    }

    getCanvasSize() {
        // Changing a <canvas> element's width/height attribute resets its 2D context to
        // defaults (including smoothing) - re-applying here (cheap, idempotent) means a crop-tool
        // resize can never silently drop back to blurry low-quality scaling.
        configureHighQualityContext(this.ctx)
        return { width: this.canvasEl.width, height: this.canvasEl.height }
    }

    getOrCreateMedia(clipId, fileId) {
        let entry = this.mediaPool.get(clipId)
        if (entry) return entry

        const source = this.resolveSource(fileId)
        if (!source) return null

        let el
        if (source.kind === 'image') {
            el = document.createElement('img')
            el.crossOrigin = 'anonymous'
            el.src = source.url
        } else {
            // Media isn't always a local blob: URL - re-editing an already-uploaded post's video
            // hands this a remote HTTPS URL instead, and drawing an unmarked cross-origin <video>
            // onto the compositor canvas taints it (silently here, but export's canvas-to-VideoFrame
            // step throws a hard SecurityError over the exact same taint). crossOrigin is a no-op
            // for actual blob: URLs, so it's safe to always set regardless of which kind this is.
            el = document.createElement('video')
            el.crossOrigin = 'anonymous'
            el.preload = 'auto'
            el.playsInline = true
            el.muted = true
            el.src = source.url
        }

        entry = { kind: source.kind, el, ready: false, retryCount: 0 }
        el.addEventListener(source.kind === 'image' ? 'load' : 'loadeddata', () => {
            entry.ready = true
            entry.retryCount = 0
            // renderAt() silently no-ops while the active clip's media isn't ready yet (there's
            // nothing decoded to draw) - on first open that left the very first frame as a plain
            // black canvas (the stage's own background) until the user scrubbed/played, since
            // nothing re-triggered a redraw once loading actually finished. Only worth doing
            // while paused - during playback tick() already redraws every frame regardless.
            if (!this.playing) this.renderAt(this.currentMs)
        })
        // A bad/expired URL, a network failure, or an unsupported codec never fires 'loadeddata' -
        // entry.ready just stayed false forever with nothing surfacing why, leaving the preview
        // black with no error and no way to recover short of closing and reopening the whole
        // editor. TimelineClip.vue's own filmstrip probe is a separate <video> element (see
        // thumbnails.js/generateFilmstrip) hitting the same URL - likely to fail for the same
        // reason, but not covered by this specific listener/retry.
        //
        // Most failures are a transient network blip rather than a truly broken URL, so this
        // retries silently on its own first (same element, fresh src) - only surfacing
        // onMediaError (the "Réessayer" banner) once every automatic attempt has also failed.
        el.addEventListener('error', () => {
            const mediaError = el.error
            console.error('[MediaEditor] media failed to load', 'fileId=', fileId, 'kind=', source.kind, 'src=', source.url, 'code=', mediaError && mediaError.code, 'message=', mediaError && mediaError.message, 'retryCount=', entry.retryCount)
            if (entry.retryCount < MAX_AUTO_MEDIA_RETRIES) {
                entry.retryCount++
                setTimeout(() => {
                    if (this.destroyed || this.mediaPool.get(clipId) !== entry) return
                    el.src = source.url
                    if (source.kind === 'video') el.load()
                }, AUTO_MEDIA_RETRY_DELAY_MS)
            } else if (this.onMediaError) {
                this.onMediaError(fileId)
            }
        })
        this.mediaPool.set(clipId, entry)
        return entry
    }

    // Discards the pooled (failed) element for this clip/fileId and rebuilds it from scratch -
    // lets the user retry after a transient failure (see the 'error' listener above) without
    // having to close and reopen the whole editor.
    retryMedia(clipId, fileId) {
        const entry = this.mediaPool.get(clipId)
        if (entry && entry.kind === 'video') {
            entry.el.removeAttribute('src')
            entry.el.load()
        }
        this.mediaPool.delete(clipId)
        this.getOrCreateMedia(clipId, fileId)
        this.renderAt(this.currentMs)
    }

    // A hard timeout fallback, not just the 'seeked' event: on a freshly-created <video> element
    // (e.g. switching to a clip whose pooled element hasn't been used yet) the browser can fail
    // to ever fire 'seeked' for the very first seek. Without this, that left switchToClip's
    // promise permanently unresolved - freezing playback on whatever was showing right before
    // the switch, since the rest of tick()'s "advance the playhead" logic never got to run again.
    // Same defensive pattern already needed for the exact same reason in thumbnails.js's filmstrip
    // frame-by-frame seeking.
    seekMediaTo(entry, sourceMs) {
        if (entry.kind === 'image') return Promise.resolve()
        const target = sourceMs / 1000
        if (Math.abs(entry.el.currentTime - target) < 0.03) return Promise.resolve()
        return new Promise((resolve) => {
            let done = false
            const finish = () => {
                if (done) return
                done = true
                entry.el.removeEventListener('seeked', finish)
                clearTimeout(timeoutId)
                resolve()
            }
            entry.el.addEventListener('seeked', finish)
            entry.el.currentTime = target
            const timeoutId = setTimeout(finish, 800)
        })
    }

    // Only the active clip's video should be audible - and at *its own* volume/mute setting
    // (clip.volume/clip.muted, the "mixage" between a clip's original sound and any background
    // music), so the live preview actually matches what the export mix (audioMixer.js) produces
    // instead of always playing the original audio at full volume regardless of that setting.
    setActiveAudioEntry(activeEntry, activeClip) {
        this.mediaPool.forEach((entry) => {
            if (entry.kind !== 'video') return
            if (entry === activeEntry) {
                entry.el.muted = !!(activeClip && activeClip.muted)
                entry.el.volume = activeClip && activeClip.volume !== undefined ? activeClip.volume : 1
            } else {
                entry.el.muted = true
            }
        })
    }

    // Crossfades the outgoing/incoming clips' own audio across the transition window, centered
    // on the same cut point as the visual blend (see getTransitionAt) - without this, the
    // outgoing clip's audio either kept playing (and getting silently paused later - see the
    // pause() right after this call) or the incoming clip's audio simply never started until
    // promotion, both of which left an audible gap of silence right in the middle of the
    // transition ("le son des 2 vidéos se baisse"). Each clip's own audio is only ever faded
    // across *its own* half of the window (silent past its own natural boundary) so this never
    // plays content beyond what the user actually trimmed. Equal-power (cos/sin, not a plain
    // linear ramp) keeps the combined perceived loudness roughly constant through the middle of
    // the fade instead of visibly dipping there.
    updateTransitionAudio(transition, globalMs) {
        const boundaryMs = transition.fromClip.timelineEndMs
        const halfDurationMs = Math.max(1, boundaryMs - transition.windowStartMs)

        let fromGain = 0
        let toGain = 0
        if (globalMs < boundaryMs) {
            const t = Math.min(Math.max((globalMs - transition.windowStartMs) / halfDurationMs, 0), 1)
            fromGain = Math.cos(t * Math.PI / 2)
        } else {
            const t = Math.min(Math.max((globalMs - boundaryMs) / halfDurationMs, 0), 1)
            toGain = Math.sin(t * Math.PI / 2)
        }

        if (this.activeEntry.kind === 'video') {
            const clipVolume = transition.fromClip.muted ? 0 : (transition.fromClip.volume !== undefined ? transition.fromClip.volume : 1)
            const volume = fromGain * clipVolume
            this.activeEntry.el.muted = volume <= 0
            this.activeEntry.el.volume = Math.min(1, Math.max(0, volume))
        }
        if (this.transitionToEntry.kind === 'video') {
            const clipVolume = transition.toClip.muted ? 0 : (transition.toClip.volume !== undefined ? transition.toClip.volume : 1)
            const volume = toGain * clipVolume
            this.transitionToEntry.el.muted = volume <= 0
            this.transitionToEntry.el.volume = Math.min(1, Math.max(0, volume))
        }
    }

    // One-off: moves playback to `clip`, starting at `effectiveMs` within it. This is the only
    // place (besides transition start) that seeks - never called on every frame.
    async switchToClip(clip, effectiveMs) {
        const entry = this.getOrCreateMedia(clip.id, clip.fileId)
        if (!entry) return null

        if (entry.kind === 'video') {
            applyClipPlaybackRate(entry.el, clip)
            const sourceMs = mapEffectiveMsToSourceMs(clip, effectiveMs)
            await this.seekMediaTo(entry, sourceMs)
            if (this.playing) entry.el.play().catch(() => {})
        } else {
            entry.imageStartedAtMs = performance.now() - effectiveMs
        }

        // The clip being left behind was never explicitly paused otherwise - it would just keep
        // playing (muted, but still decoding) in the background indefinitely, one more <video>
        // silently accumulating for every cut made during a session.
        if (this.activeEntry && this.activeEntry !== entry && this.activeEntry.kind === 'video') {
            this.activeEntry.el.pause()
        }

        this.activeClip = clip
        this.activeEntry = entry
        this.skippingToSegmentStart = null
        this.overrunAnchorRealMs = null
        // Cleared so the *next* upcoming clip is free to be pre-seeked ahead of the following cut
        // (see preSeekUpcomingClip) - left pinned to this now-current clip's id, it would otherwise
        // permanently block any future pre-seek attempt for the rest of the timeline.
        this.preSeekedClipId = null
        this.setActiveAudioEntry(entry, clip)
        return entry
    }

    // Kicks off the *next* clip's seek a little before the actual cut, exactly like the transition
    // path already does for its own incoming clip (see tick()'s transition branch) - a plain hard
    // cut had no equivalent: switchToClip only ever started seeking right at the boundary itself,
    // and a real seek (especially the first one on a freshly preloaded element) can easily take
    // 100-800ms to actually settle, all of it spent frozen on the outgoing clip's last frame. Doing
    // the same seek this far ahead instead means it's normally already sitting at the right frame
    // by the time the cut actually happens, so switchToClip's own seekMediaTo resolves instantly.
    preSeekUpcomingClip(clip) {
        if (this.preSeekedClipId === clip.id) return
        this.preSeekedClipId = clip.id

        const entry = this.getOrCreateMedia(clip.id, clip.fileId)
        if (!entry || entry.kind !== 'video') return
        applyClipPlaybackRate(entry.el, clip)
        this.seekMediaTo(entry, mapEffectiveMsToSourceMs(clip, 0))
    }

    // Reads the *current* effective position of the active clip from its native playback state,
    // without seeking. Returns null if we've drifted into a disabled segment (rare).
    readActiveEffectiveMs(now) {
        if (!this.activeClip || !this.activeEntry) return 0

        if (this.activeEntry.kind === 'image') {
            return now - this.activeEntry.imageStartedAtMs
        }

        const sourceLocalMs = this.activeEntry.el.currentTime * 1000 - this.activeClip.trimStartMs
        return mapSourceMsToEffectiveMs(this.activeClip, sourceLocalMs)
    }

    getCanvasBackground() {
        const { backgroundMode, backgroundColor } = this.getTimeline().canvas
        return { mode: backgroundMode, color: backgroundColor }
    }

    drawSingle(clip, entry) {
        renderClipFrame(this.ctx, entry.el, this.getCanvasSize(), clip, {
            cropPreview: clip.id === this.getCropPreviewClipId(),
            background: this.getCanvasBackground()
        })
    }

    // Cheap, synchronous redraw for a live overlay drag/resize/rotate tick - reuses whatever
    // video frame is already decoded (no seeking) and just recomposites the freshly-updated
    // overlay layer on top of it. Deliberately NOT renderAt(): that one is async (awaits media
    // seeks), and calling it on every single interaction tick (many times a second while
    // dragging) let multiple calls overlap and interleave their canvas draws in an unpredictable
    // order - a real race that could paint a half-updated frame (e.g. video frame drawn by a
    // slower call landing *after* a faster call's overlay layer, blanking or corrupting it).
    redrawOverlaysOnly() {
        if (this.activeClip && this.activeEntry) {
            this.drawSingle(this.activeClip, this.activeEntry)
        }
        this.drawOverlays()
    }

    drawTransition(transition, fromEntry, toEntry) {
        renderTransitionFrame(this.ctx, {
            fromMediaEl: fromEntry.el,
            fromClip: transition.fromClip,
            toMediaEl: toEntry.el,
            toClip: transition.toClip,
            canvasSize: this.getCanvasSize(),
            type: transition.type,
            direction: transition.direction,
            progress: transition.progress,
            background: this.getCanvasBackground()
        })
    }

    drawOverlays() {
        const overlayCanvasEl = this.getOverlayCanvasEl && this.getOverlayCanvasEl()
        renderOverlayLayer(this.ctx, overlayCanvasEl, this.getCanvasSize())
    }

    // Keeps one <audio> element per background music clip in sync with the global playhead -
    // several tracks can be laid out sequentially or overlapping, each gets its own element so
    // the browser just mixes whichever ones are simultaneously active.
    syncBackgroundAudio(globalMs) {
        const audioTrack = getAudioTrack(this.getTimeline())
        const clips = audioTrack ? audioTrack.clips : []
        const activeIds = new Set()

        clips.forEach((clip) => {
            const active = globalMs >= clip.timelineStartMs && globalMs < clip.timelineEndMs
            if (!active) return

            let audioEl = this.backgroundAudioEls.get(clip.id)
            if (!audioEl || this.backgroundAudioFileIds.get(clip.id) !== clip.fileId) {
                const source = this.resolveSource(clip.fileId)
                if (!source) return
                if (audioEl) audioEl.pause()
                audioEl = new Audio(source.url)
                this.backgroundAudioEls.set(clip.id, audioEl)
                this.backgroundAudioFileIds.set(clip.id, clip.fileId)
            }

            activeIds.add(clip.id)
            audioEl.volume = clip.muted ? 0 : (clip.volume !== undefined ? clip.volume : 1)

            const targetSec = (clip.trimStartMs + (globalMs - clip.timelineStartMs)) / 1000
            if (Math.abs(audioEl.currentTime - targetSec) > 0.4) audioEl.currentTime = targetSec
            if (this.playing && audioEl.paused) audioEl.play().catch(() => {})
            if (!this.playing) audioEl.pause()
        })

        this.backgroundAudioEls.forEach((audioEl, clipId) => {
            if (!activeIds.has(clipId)) audioEl.pause()
        })
    }

    // One-off, frame-accurate render for a specific global timestamp - used for scrubbing/seek
    // and the initial frame. Not used while actively playing (see tick()).
    async renderAt(globalMs) {
        const timeline = this.getTimeline()
        const mediaTrack = getMediaTrack(timeline)
        if (!mediaTrack || !mediaTrack.clips.length) return

        this.transitionToClip = null
        this.transitionToEntry = null

        const transition = getTransitionAt(mediaTrack.clips, globalMs)
        if (transition) {
            const fromEntry = this.getOrCreateMedia(transition.fromClip.id, transition.fromClip.fileId)
            const toEntry = this.getOrCreateMedia(transition.toClip.id, transition.toClip.fileId)
            if (!fromEntry || !toEntry || !fromEntry.ready || !toEntry.ready) return

            const fromSourceMs = mapEffectiveMsToSourceMs(transition.fromClip, globalMs - transition.fromClip.timelineStartMs)
            // See exportTimeline.js's identical comment - the transition window is centered on
            // the cut, so toClip needs to actually advance past frame 0 once globalMs crosses its
            // own timelineStartMs, not stay frozen for the whole window.
            const toSourceMs = mapEffectiveMsToSourceMs(transition.toClip, Math.max(0, globalMs - transition.toClip.timelineStartMs))
            await Promise.all([this.seekMediaTo(fromEntry, fromSourceMs), this.seekMediaTo(toEntry, toSourceMs)])

            this.activeClip = transition.fromClip
            this.activeEntry = fromEntry
            this.setActiveAudioEntry(fromEntry, transition.fromClip)
            this.drawTransition(transition, fromEntry, toEntry)
        } else {
            const clip = getActiveClipAtMs(timeline, globalMs)
            if (!clip) return
            const entry = this.getOrCreateMedia(clip.id, clip.fileId)
            if (!entry || !entry.ready) return

            const effectiveMs = globalMs - clip.timelineStartMs
            await this.switchToClip(clip, effectiveMs)
            if (!this.playing && entry.kind === 'video') entry.el.pause()
            this.drawSingle(clip, entry)
        }

        this.drawOverlays()
    }

    tick(now) {
        if (this.destroyed || !this.playing) return

        const timeline = this.getTimeline()
        const mediaTrack = getMediaTrack(timeline)
        if (!mediaTrack || !mediaTrack.clips.length || !this.activeClip) {
            this.pause()
            return
        }

        const effectiveMs = this.readActiveEffectiveMs(now)

        if (effectiveMs === null) {
            // Drifted into a disabled segment - jump past it. Seeking is asynchronous: currentTime
            // doesn't reach the target instantly, so effectiveMs stays null for several ticks while
            // it's in flight. Without skippingToSegmentStart as a guard, every one of those ticks
            // re-issued the *same* seek, which restarts the browser's already-in-progress seek
            // instead of letting it finish - that's what compounded a single quick seek into a
            // multi-second stall each time a silent segment was skipped.
            const sourceLocalMs = this.activeEntry.el.currentTime * 1000 - this.activeClip.trimStartMs
            const nextStart = findNextEnabledSegmentStart(this.activeClip, sourceLocalMs)
            if (nextStart !== null && this.skippingToSegmentStart !== nextStart) {
                this.skippingToSegmentStart = nextStart
                this.activeEntry.el.currentTime = (this.activeClip.trimStartMs + nextStart) / 1000
            }
            this.rafHandle = requestAnimationFrame(this.tick)
            return
        }
        this.skippingToSegmentStart = null

        // A centered transition (see getTransitionAt) needs globalMs to keep advancing *past*
        // this clip's own natural end for the second half of the window - but effectiveMs (via
        // mapSourceMsToEffectiveMs) permanently plateaus at the clip's own effective duration the
        // instant its <video> plays past trimEnd, since that function has no way to tell "just
        // reached the end" apart from "still sitting well past it". Left alone, that plateau
        // would freeze globalMs (and therefore the whole transition's progress) forever the
        // moment the outgoing clip's video reaches its own end - exactly the "fondu qui ne se
        // termine jamais" bug. Once plateaued, switch to advancing via wall-clock time instead,
        // anchored to the instant the plateau was first reached.
        let globalMs
        const plateaued = this.activeEntry.kind === 'video' && effectiveMs >= getEffectiveClipDuration(this.activeClip)
        if (plateaued) {
            if (this.overrunAnchorRealMs === null) {
                this.overrunAnchorGlobalMs = this.activeClip.timelineStartMs + effectiveMs
                this.overrunAnchorRealMs = now
            }
            globalMs = this.overrunAnchorGlobalMs + (now - this.overrunAnchorRealMs)
        } else {
            this.overrunAnchorRealMs = null
            globalMs = this.activeClip.timelineStartMs + effectiveMs
        }
        const durationMs = computeTimelineDurationMs(timeline)

        if (globalMs >= durationMs) {
            this.currentMs = durationMs
            this.onTimeUpdate(Math.round(this.currentMs))
            this.pause()
            return
        }

        const transition = getTransitionAt(mediaTrack.clips, globalMs)

        if (transition && transition.fromClip.id === this.activeClip.id) {
            if (!this.transitionToClip || this.transitionToClip.id !== transition.toClip.id) {
                // Entering a new transition window: start the incoming clip playing in parallel.
                const toEntry = this.getOrCreateMedia(transition.toClip.id, transition.toClip.fileId)
                if (toEntry) {
                    if (toEntry.kind === 'video') applyClipPlaybackRate(toEntry.el, transition.toClip)
                    const toSourceMs = mapEffectiveMsToSourceMs(transition.toClip, 0)
                    this.seekMediaTo(toEntry, toSourceMs).then(() => {
                        if (this.playing && toEntry.kind === 'video') toEntry.el.play().catch(() => {})
                    })
                    this.transitionToClip = transition.toClip
                    this.transitionToEntry = toEntry
                }
            }

            if (this.transitionToEntry && this.transitionToEntry.ready) {
                this.updateTransitionAudio(transition, globalMs)
                // The centered window now extends durationMs/2 *past* this clip's own natural end
                // (transition.fromClip.timelineEndMs) - past that point its <video> would
                // otherwise keep decoding forward on its own into whatever raw footage follows
                // trimEndMs in the source file. Holding it here shows its actual last (trimmed)
                // frame for that second half instead, exactly like a natural cut would. Its audio
                // has already been faded fully out by this same point (see updateTransitionAudio),
                // so pausing here doesn't cut anything audible off.
                if (this.activeEntry.kind === 'video' && globalMs >= transition.fromClip.timelineEndMs && !this.activeEntry.el.paused) {
                    this.activeEntry.el.pause()
                }
                this.drawTransition(transition, this.activeEntry, this.transitionToEntry)
            } else {
                this.drawSingle(this.activeClip, this.activeEntry)
            }
        } else if (this.transitionToClip) {
            // We just exited the transition window: promote the (already playing) incoming clip.
            if (this.activeEntry && this.activeEntry !== this.transitionToEntry && this.activeEntry.kind === 'video') {
                this.activeEntry.el.pause()
            }
            this.activeClip = this.transitionToClip
            this.activeEntry = this.transitionToEntry
            // A video's position is already correct here (it was seeked into place - and kept
            // playing - back when the transition window started, see above). An image has no such
            // native position to fall back on - only switchToClip ever sets imageStartedAtMs, and
            // a clip promoted out of a transition never goes through it. Without this, the next
            // tick reads `now - undefined` (NaN) as this image's effective position, which compares
            // false against every boundary check below and effectively wedges playback in place.
            if (this.activeEntry.kind === 'image') this.activeEntry.imageStartedAtMs = now
            this.setActiveAudioEntry(this.activeEntry, this.activeClip)
            this.transitionToClip = null
            this.transitionToEntry = null
            this.overrunAnchorRealMs = null
            globalMs = this.activeClip.timelineStartMs
            this.drawSingle(this.activeClip, this.activeEntry)
        } else {
            // Only ever the *immediate* next clip in the track, never whatever getActiveClipAtMs
            // would resolve globalMs to. An image clip's effective position is read straight off
            // the wall clock (see readActiveEffectiveMs) with nothing bounding it while a switch
            // to it is still in flight (unlike a <video>, whose currentTime can't run past its own
            // real duration on its own) - if that switch happens to take a while, globalMs can
            // overshoot past more than one clip boundary before it resolves. Clamping to "one clip
            // at a time" means a stall shows up as a brief pause/catch-up at most, never as
            // silently skipping the clip that was supposed to play right after.
            const currentIndex = mediaTrack.clips.findIndex(c => c.id === this.activeClip.id)
            const expectedClip = currentIndex === -1 ? getActiveClipAtMs(timeline, globalMs) : mediaTrack.clips[currentIndex + 1]

            if (expectedClip && globalMs >= expectedClip.timelineStartMs && expectedClip.id !== this.activeClip.id) {
                // Natural cut with no transition - switch (one seek, not per-frame). Guard
                // against re-triggering the switch on every frame while the seek is in flight.
                if (this.switchingToClipId !== expectedClip.id) {
                    this.switchingToClipId = expectedClip.id
                    this.switchToClip(expectedClip, 0).finally(() => { this.switchingToClipId = null })
                }
                globalMs = expectedClip.timelineStartMs
                // Keep showing the outgoing clip's last frame until the switch resolves, rather
                // than drawing with a half-updated activeClip/activeEntry pair.
                this.drawSingle(this.activeClip, this.activeEntry)
            } else {
                // Still within this clip, but close enough to its end that the *next* one's seek
                // is worth starting now rather than waiting for the boundary itself (see
                // preSeekUpcomingClip).
                if (expectedClip && expectedClip.id !== this.activeClip.id && expectedClip.timelineStartMs - globalMs <= CUT_PRESEEK_LOOKAHEAD_MS) {
                    this.preSeekUpcomingClip(expectedClip)
                }
                this.drawSingle(this.activeClip, this.activeEntry)
            }
        }

        this.drawOverlays()

        this.currentMs = globalMs
        this.syncBackgroundAudio(this.currentMs)
        this.onTimeUpdate(Math.round(this.currentMs))

        this.rafHandle = requestAnimationFrame(this.tick)
    }

    async play() {
        if (this.destroyed || this.playing) return
        const token = ++this.playToken

        const timeline = this.getTimeline()
        const durationMs = computeTimelineDurationMs(timeline)
        if (this.currentMs >= durationMs) this.currentMs = 0

        this.playing = true

        const clip = getActiveClipAtMs(timeline, this.currentMs)
        if (clip) {
            await this.switchToClip(clip, this.currentMs - clip.timelineStartMs)
        }

        // A pause() (or another play()) happened while we were awaiting above - don't clobber
        // whatever state it already settled into (this is what made rapid play/pause clicks,
        // especially while also dragging a widget, leave playback in a confused state).
        if (token !== this.playToken) return
        if (!this.playing) return

        this.lastTickAt = null
        this.rafHandle = requestAnimationFrame(this.tick)
    }

    pause() {
        this.playToken++
        this.playing = false
        this.mediaPool.forEach((entry) => { if (entry.kind === 'video') entry.el.pause() })
        this.backgroundAudioEls.forEach((audioEl) => audioEl.pause())
        if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle)
        this.rafHandle = null
    }

    seek(globalMs) {
        const durationMs = computeTimelineDurationMs(this.getTimeline())
        this.currentMs = Math.min(Math.max(globalMs, 0), durationMs)
        // Returned (not fire-and-forget) so a caller that needs the frame actually drawn before
        // doing something else with the canvas (see VideoPreviewCanvas.vue's getSnapshotDataUrl
        // via MediaEditor.vue's finish()) can await it instead of racing renderAt's own seek/decode.
        const renderPromise = this.renderAt(this.currentMs)
        this.syncBackgroundAudio(this.currentMs)
        this.onTimeUpdate(Math.round(this.currentMs))
        return renderPromise
    }

    // Eagerly creates (and starts loading) the media element for every clip, so a natural cut
    // never lands on a freshly-created, not-yet-loaded <video> mid-playback.
    preloadAllClips() {
        const mediaTrack = getMediaTrack(this.getTimeline())
        if (!mediaTrack) return
        mediaTrack.clips.forEach(clip => this.getOrCreateMedia(clip.id, clip.fileId))
    }

    // Call after the clip list changes (add/remove/reorder/trim) so a stale frame isn't shown.
    refresh() {
        this.preloadAllClips()
        return this.renderAt(this.currentMs)
    }

    destroy() {
        this.pause()
        this.destroyed = true
        this.backgroundAudioEls.forEach((audioEl) => {
            audioEl.pause()
            audioEl.removeAttribute('src')
        })
        this.backgroundAudioEls.clear()
        this.backgroundAudioFileIds.clear()
        this.mediaPool.forEach((entry) => {
            if (entry.kind === 'video') {
                entry.el.pause()
                entry.el.removeAttribute('src')
                entry.el.load()
            }
        })
        this.mediaPool.clear()
    }
}
